/**
 * Standalone "Differences overview" modal (WHA-3695 follow-up). A data-only IFC
 * diff with NO 3D viewer / glTF load, launched from the documents list so users
 * can scan what changed between two .ifc files instantly, then optionally open
 * the heavy 3D compare view.
 *
 * Layout: header with the OLDER/NEWER version plaque + "Open in 3D viewer",
 * then a master–detail body — summary sidebar | element browser | per-element
 * detail drawer that slides in on selection.
 *
 * Either side of the diff can be re-targeted from the plaque's file pickers
 * (candidates are the building's processed .ifc files, narrowed to the header's
 * active project scope); changing a file re-keys the diff query. `observer`
 * because the project-scope hook reads MST observables.
 *
 * Reuses the same browser component as the 3D rail; selection/visibility are
 * local React state here (the rail uses the IfcModelStore instead).
 */

import { useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Box, CheckCircle2 } from "lucide-react";

import { FranklinLogo } from "@/components/ui/franklin-logo";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import type { IfcChangeType } from "@/stores/ifcModel/repository";
import { useIfcDiff } from "@/domains/buildings/hooks/useIfcDiff";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import {
  candidateBadgeVersion,
  useIfcModelCandidates,
} from "@/domains/buildings/hooks/useIfcModelCandidates";
import { IfcDiffBrowser } from "./IfcDiffBrowser";
import { IfcDiffDetailDrawer } from "./IfcDiffDetailDrawer";
import { IfcDiffOverviewSidebar } from "./IfcDiffOverviewSidebar";
import { IfcDiffVersionPlaque, type IfcFileChip } from "./IfcDiffVersionPlaque";
import { useModelViewerGuard } from "@/hooks/usePermissionDenied";

interface IfcDiffOverviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  baseDocId: number;
  targetDocId: number;
  baseFilename: string;
  baseVersion: number | null;
  targetFilename: string;
  targetVersion: number | null;
  /** Building the 3D viewer route is scoped to (for "Open in 3D viewer"). */
  buildingId: number | string;
}

function chipLabel(chip: IfcFileChip): string {
  return chip.version != null ? `${chip.filename} v${chip.version}` : chip.filename;
}

export const IfcDiffOverviewDialog = observer(function IfcDiffOverviewDialog({
  open,
  onOpenChange,
  baseDocId,
  targetDocId,
  baseFilename,
  baseVersion,
  targetFilename,
  targetVersion,
  buildingId,
}: IfcDiffOverviewDialogProps) {
  const { t, language } = useLanguage();
  const tv = t.buildings.ifcViewer;
  const navigate = useNavigate();
  const guardModelViewer = useModelViewerGuard();

  const [selectedGuid, setSelectedGuid] = useState<string | null>(null);
  // Change-type FILTERS (modal-variant browser semantics): none selected shows
  // every change; selecting chips narrows the list to those types. Named
  // typeFilters (not visibility) because the semantics are inverted vs the 3D
  // rail's on/off visibility toggles — do not persist/read this as visibility.
  const [typeFilters, setTypeFilters] = useState({
    added: false,
    removed: false,
    modified: false,
  });
  // The two compared documents. The diff is directional (older→newer:
  // only-in-newer = added, only-in-older = removed). Both sides are
  // re-targetable via the plaque pickers; swapping exchanges them. Mounted
  // fresh per open (conditional render), so init-from-props is safe.
  const [olderDocId, setOlderDocId] = useState(baseDocId);
  const [newerDocId, setNewerDocId] = useState(targetDocId);

  const { selectedProjectId } = useBuildingScope(String(buildingId));
  const { currentOrganization } = useOrganizationContext();
  const { candidates, versionGroupCounts } = useIfcModelCandidates(
    Number(buildingId),
    currentOrganization?.id,
    selectedProjectId,
    open
  );

  const candById = useMemo(() => new Map(candidates.map((c) => [c.docId, c])), [candidates]);
  // Fallback labels for the two documents we opened with, in case a side is
  // showing one of them before candidates have loaded (e.g. right after swap).
  const initialLabels = useMemo(
    () =>
      new Map<number, IfcFileChip>([
        [baseDocId, { filename: baseFilename, version: baseVersion }],
        [targetDocId, { filename: targetFilename, version: targetVersion }],
      ]),
    [baseDocId, targetDocId, baseFilename, baseVersion, targetFilename, targetVersion]
  );

  // Every label resolved from candidates so far, so a side keeps its chip even
  // if its doc later drops out of `candidates` (project scope narrowing, an
  // org-key refetch) — initialLabels only covers the pair the dialog opened with.
  const knownLabels = useRef(new Map<number, IfcFileChip>());

  const chipFor = (docId: number): IfcFileChip => {
    const c = candById.get(docId);
    if (c) {
      const chip = { filename: c.filename, version: candidateBadgeVersion(c, versionGroupCounts) };
      knownLabels.current.set(docId, chip);
      return chip;
    }
    return (
      knownLabels.current.get(docId) ?? initialLabels.get(docId) ?? { filename: "", version: null }
    );
  };
  const olderChip = chipFor(olderDocId);
  const newerChip = chipFor(newerDocId);

  const {
    data: diff,
    isPending,
    isError,
  } = useIfcDiff(open ? olderDocId : null, open ? newerDocId : null);

  const selectedChange = useMemo(
    () => diff?.changes.find((c) => c.guid === selectedGuid) ?? null,
    [diff, selectedGuid]
  );

  const identical =
    !!diff && diff.summary.added + diff.summary.removed + diff.summary.modified === 0;

  // Any change to either side flips element change_types, so drop the selection.
  const handleSwap = () => {
    setSelectedGuid(null);
    setOlderDocId(newerDocId);
    setNewerDocId(olderDocId);
  };
  const handlePickOlder = (docId: number) => {
    if (docId === olderDocId) return; // re-picking the shown file — keep selection
    setSelectedGuid(null);
    setOlderDocId(docId);
  };
  const handlePickNewer = (docId: number) => {
    if (docId === newerDocId) return;
    setSelectedGuid(null);
    setNewerDocId(docId);
  };

  const openIn3d = () => {
    onOpenChange(false);
    // /model is a projects-only route, and BuildingLayout gates every
    // projects-mode route behind a concrete project — so landing there
    // scope-less from Building view trips its "choose a project" prompt.
    // Carry the compared docs' project into the URL: useBuildingScope treats
    // a valid ?project= on a non-index route as an explicit deep link and
    // adopts Projects view with that scope silently. Prefer the newer side's
    // project; a doc with no project falls back to the other side, then to
    // the remembered scope (no param).
    const projectId = candById.get(newerDocId)?.projectId ?? candById.get(olderDocId)?.projectId;
    const scope = projectId != null ? `&project=${projectId}` : "";
    guardModelViewer(projectId, () =>
      navigate(`/buildings/${buildingId}/model?compare=${olderDocId},${newerDocId}${scope}`)
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] max-w-7xl flex-col gap-0 overflow-hidden p-0">
        {/* Three-column header grid keeps the plaque dead-center regardless of
            how wide the title and the 3D button are. */}
        <DialogHeader className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 space-y-0 border-b border-border px-4 py-3 pr-12">
          <div className="min-w-0 justify-self-start">
            <DialogTitle>{tv.diffOverviewTitle}</DialogTitle>
            {/* The plaque replaces the visible subtitle; keep an sr-only
                description for Radix a11y. */}
            <DialogDescription className="sr-only">
              {chipLabel(newerChip)} vs {chipLabel(olderChip)}
            </DialogDescription>
          </div>
          <IfcDiffVersionPlaque
            older={olderChip}
            newer={newerChip}
            onSwap={handleSwap}
            tv={tv}
            candidates={candidates}
            versionGroupCounts={versionGroupCounts}
            olderDocId={olderDocId}
            newerDocId={newerDocId}
            onPickOlder={handlePickOlder}
            onPickNewer={handlePickNewer}
          />
          {/* Red by explicit product decision — it's the modal's primary escape
              hatch and is meant to stand out. Styled red directly rather than via
              the `destructive` variant: this action destroys nothing, and that
              variant is reserved for genuinely destructive ones. Do not "fix"
              this back to outline. */}
          <Button
            size="sm"
            className="shrink-0 justify-self-end bg-red-600 text-white hover:bg-red-700"
            onClick={openIn3d}
          >
            <Box className="mr-1.5 size-4" />
            {tv.openIn3dViewer}
          </Button>
        </DialogHeader>

        {isPending ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
            <FranklinLogo animated size="xl" className="h-20 w-20" imageClassName="h-14 w-14" />
            {tv.compareLoading}
          </div>
        ) : isError || !diff ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            {tv.compareFailed}
          </div>
        ) : identical ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
            <CheckCircle2 className="size-8 text-emerald-500" />
            <p className="text-sm font-medium">
              {chipLabel(newerChip)} vs {chipLabel(olderChip)}
            </p>
            <p className="text-sm text-muted-foreground">{tv.compareNoChanges}</p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1">
            {/* Left: summary rail */}
            <IfcDiffOverviewSidebar diff={diff} tv={tv} />

            {/* Center: grouped browser (no 3D linkage in the modal) */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <IfcDiffBrowser
                changes={diff.changes}
                storeys={diff.storeys}
                visibility={typeFilters}
                onToggleVisibility={(c: IfcChangeType) =>
                  setTypeFilters((v) => ({ ...v, [c]: !v[c] }))
                }
                selectedGuid={selectedGuid}
                onSelect={setSelectedGuid}
                language={language}
                tv={tv}
                variant="modal"
              />
              {!selectedChange && (
                <p className="px-4 py-2 text-xs text-muted-foreground">{tv.compareSelectPrompt}</p>
              )}
            </div>

            {/* Right: per-element detail drawer (slides in on selection) */}
            <IfcDiffDetailDrawer
              change={selectedChange}
              olderDocId={olderDocId}
              newerDocId={newerDocId}
              storeys={diff.storeys}
              onClose={() => setSelectedGuid(null)}
              tv={tv}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});
